import { FUEL_CATEGORY } from './vehicle'
import type { Category, FuelKind, Trip, Vignette } from './types'

/**
 * Startbestückung des Roadbooks: die geplante Route Schweiz - Balkan -
 * Schwarzes Meer. Sie wird beim ersten Start angelegt und danach nicht mehr
 * angefasst; alles Weitere passiert über die Bearbeitung in der App.
 */
export const SEED_TRIP: Trip = {
  meta: {
    title: 'Schweiz → Balkan → Schwarzes Meer',
    // Name und Verbrauch stehen zusätzlich hier, wie in jeder Reise: siehe
    // mitFahrzeug in lib/vehicle.ts.
    vehicle: 'VW T3 Camper',
    crew: 2,
    start: '2026-08-17',
    consumption: 14,
    fuelPrice: 1.75,
    currency: 'EUR',
  },
  /**
   * Das Fahrzeug der Beispielreise.
   *
   * Benziner, kein Diesel. Ein Wasserboxer im beladenen Camper über Pässe
   * liegt eher bei 14 als bei den 12 Litern, mit denen die Planung gerechnet
   * hat, und der Zuschlag von einem Viertel auf die Fahrzeit des Routendienstes
   * trifft einen Bus, der 90 bis 100 fährt, ungefähr. Alles davon sind
   * Schätzwerte und im Reiter Daten änderbar; sobald ihr die ersten Male
   * getankt habt, tragt die echten Zahlen ein.
   *
   * Sie stehen hier und nicht mehr im Programm - eine zweite Reise mit einem
   * Personenwagen erbt von diesem Fahrzeug nichts.
   */
  vehicle: {
    name: 'VW T3 Camper',
    fuel: 'benzin',
    consumption: 14,
    // 60 Liter, der Serientank. Damit sind es rund 430 km bis zur nächsten
    // Tankstelle - auf dem Balkan keine akademische Frage.
    tank: 60,
    surcharge: 0.25,
    sign: 'CH',
  },
  stages: [
    {
      id: 's01',
      day: 1,
      from: 'Bern',
      to: 'Bled',
      c1: [46.95, 7.44],
      c2: [46.369, 14.114],
      km: 550,
      h: 6.5,
      land: 'CH→SI',
      type: 'transit',
      camping: [
        {
          name: 'Camping Bled (Sava)',
          price: '40–50 €',
          note: '150 m zum See, im August reservieren',
        },
        { name: 'Kamp Šobec, Lesce', price: '~40 €', note: 'am Fluss, familiär' },
      ],
      highlights: ['Karawankentunnel 8.80 € extra', 'Bleder See, Altstadt'],
      night: [],
      tip: 'Früh los — Karawankentunnel staut am Wochenende.',
    },
    {
      id: 's02',
      day: 2,
      from: 'Bled',
      to: 'Bled / Bohinj',
      c1: [46.369, 14.114],
      c2: [46.28, 13.87],
      km: 60,
      h: 1.5,
      land: 'SI',
      type: 'standtag',
      camping: [{ name: 'Camping Bled', price: '40–50 €', note: '2. Nacht' }],
      highlights: [
        'Vintgar-Klamm 10 € p.P.',
        'Zeitfenster 48 h vorher online buchen',
        'Helm Pflicht, Einbahnweg 45–60 Min.',
      ],
      night: [],
      tip: 'Vintgar um 8 Uhr = leer. Später Bohinjsee zum Baden.',
    },
    {
      id: 's03',
      day: 3,
      from: 'Bled',
      to: 'Bovec (Soča)',
      c1: [46.369, 14.114],
      c2: [46.338, 13.552],
      // Ohne diesen Punkt führt der Routendienst die Etappe durch den
      // Karawankentunnel und über den Predel: 108 statt 84 Kilometer, durch
      // Österreich und Italien - und am Vršič vorbei, den der Hinweis dieser
      // Etappe und ihre Tulpen beschreiben. Die Passhöhe zwingt die Strecke
      // auf die Strasse, um die es geht.
      via: [{ name: 'Vršič-Pass', at: [46.43478, 13.74371] }],
      km: 100,
      h: 3,
      land: 'SI',
      type: 'pass',
      camping: [
        { name: 'Kamp Koren, Kobarid', price: '~35 €', note: 'Klassiker am Fluss' },
        { name: 'Kamp Soča', price: '~30 €', note: 'direkt am Türkiswasser' },
      ],
      highlights: ['Vršič-Pass, 50 Kehren', 'Soča-Trail', 'Wasser ~10 °C — kurz und schmerzhaft'],
      night: [],
      tip: 'Vršič im 2. Gang, Kühlwasser im Auge behalten.',
    },
    {
      id: 's04',
      day: 4,
      from: 'Bovec',
      to: 'Plitvice',
      c1: [46.338, 13.552],
      c2: [44.881, 15.616],
      km: 300,
      h: 5,
      land: 'SI→HR',
      type: 'transit',
      camping: [{ name: 'Camping Korana', price: '~30 €', note: 'parkeigen, 7 km vom Eingang' }],
      highlights: [
        'Plitvice 40 € p.P. (ab 16 Uhr 25 €)',
        'max. 300 Pers./Std. — online vorbuchen!',
        'Ticket inkl. Bahn + Boot',
      ],
      night: [],
      tip: 'Slot für 7:00 Uhr Eingang 1 buchen. Slots sind wochenlang vorher weg.',
    },
    {
      id: 's05',
      day: 5,
      from: 'Plitvice',
      to: 'Zadar',
      c1: [44.881, 15.616],
      c2: [44.119, 15.231],
      km: 130,
      h: 2,
      land: 'HR',
      type: 'kurz',
      camping: [
        {
          name: 'Zaton Holiday Resort',
          price: '45–60 €',
          note: '1.5 km Sandstrand, Shuttle nach Zadar 10:00 / zurück 18:15',
        },
        { name: 'Camping Peros, Zaton', price: '~30 €', note: 'klein, günstig' },
      ],
      highlights: ['Meeresorgel & Gruss an die Sonne', 'Sonnenuntergang an der Riva'],
      night: [
        'The Garden Lounge — auf der Stadtmauer, DJs bis Sonnenaufgang',
        'Svarog Bar — open air bis 5 Uhr',
        'Opera Club — Waterfront, house/EDM',
        'Ledana Lounge, Arsenal',
      ],
      tip: 'Erster grosser Abend der Reise. Shuttle nutzen, nicht fahren.',
    },
    {
      id: 's06',
      day: 6,
      from: 'Zadar',
      to: 'Zadar / Pag',
      c1: [44.119, 15.231],
      c2: [44.55, 14.88],
      km: 160,
      h: 2.5,
      land: 'HR',
      type: 'standtag',
      camping: [{ name: 'Zaton Holiday Resort', price: '45–60 €', note: '2. Nacht' }],
      highlights: ['Strandtag', 'optional Zrće Beach / Novalja auf Pag (~1.5 h)'],
      night: [
        'Zrće 2026 offen: Kalypso, Papaya, Aquarius, Nomad, LIFT',
        'NOA und Rocks sind zu — nicht mehr suchen',
      ],
      tip: 'Zrće heisst: nicht selbst fahren. Bus ab Novalja.',
    },
    {
      id: 's07',
      day: 7,
      from: 'Zadar',
      to: 'Una NP (Bihać)',
      c1: [44.119, 15.231],
      c2: [44.814, 15.871],
      km: 180,
      h: 3,
      land: 'HR→BA',
      type: 'grenze',
      camping: [
        { name: 'Camps in Kulen Vakuf', price: '15–25 €', note: 'einfach, am Fluss, T3-tauglich' },
      ],
      highlights: [
        'Štrbački Buk ~7 KM Eintritt',
        'NUR BARGELD — vorher in Bihać abheben',
        'Rafting / Kajak',
      ],
      night: [],
      tip: 'Grüne Karte als Papierausdruck bereithalten. Reisepass statt ID.',
    },
    {
      id: 's08',
      day: 8,
      from: 'Una NP',
      to: 'Sarajevo',
      c1: [44.814, 15.871],
      c2: [43.856, 18.413],
      km: 300,
      h: 4.5,
      land: 'BA',
      type: 'transit',
      camping: [{ name: 'Camps um Ilidža', price: '20–30 €', note: 'Stadtrand, Tram ins Zentrum' }],
      highlights: ['Baščaršija', 'Tunnel of Hope', 'Ende August: Sarajevo Film Festival'],
      night: [
        'Sloga — altes Kino, Studenten, Live, günstig',
        'Birtija — Rakija ab ~2 €',
        'Muvekita / Zelenih Beretki',
      ],
      tip: 'Wenn das Filmfestival läuft: hier zwei Nächte einplanen.',
    },
    {
      id: 's09',
      day: 9,
      from: 'Sarajevo',
      to: 'Đerdap / Golubac',
      c1: [43.856, 18.413],
      c2: [44.657, 21.634],
      km: 450,
      h: 6.5,
      land: 'BA→RS',
      type: 'lang',
      camping: [
        {
          name: 'Camps am Silver Lake (Srebrno jezero)',
          price: '20–30 €',
          note: 'Marina, Strand, Pools',
        },
        { name: 'Donji Milanovac', price: '~20 €', note: 'direkt an der Donau' },
      ],
      highlights: ['Golubac-Festung', 'Đerdap-Schlucht M25', 'Lepenski Vir'],
      night: ['Alternative: Niš statt Đerdap — kürzer und Studentenstadt'],
      tip: 'DIESE ETAPPE ist der Grund für die Routenkorrektur. Sarajevo→Belogradchik direkt sind 600 km / 9 h.',
    },
    {
      id: 's10',
      day: 10,
      from: 'Đerdap',
      to: 'Veliko Tarnovo',
      c1: [44.657, 21.634],
      c2: [43.081, 25.629],
      km: 350,
      h: 5,
      land: 'RS→BG',
      type: 'grenze',
      camping: [{ name: 'Camps um Veliko Tarnovo', price: '15–25 €', note: 'spontan meist ok' }],
      highlights: [
        'Zwischenstopp Belogradchik: Felsen + Festung Kaleto',
        'Festung Zarevec, Ton- und Lichtshow',
      ],
      night: ['Studentenbars in der Altstadt, Samovodska Charshia'],
      tip: 'Bulgarien-Vignette digital vor der Grenze kaufen (bgtoll.bg).',
    },
    {
      id: 's11',
      day: 11,
      from: 'Veliko Tarnovo',
      to: 'Sozopol',
      c1: [43.081, 25.629],
      c2: [42.418, 27.696],
      km: 250,
      h: 3.5,
      land: 'BG',
      type: 'kurz',
      camping: [
        { name: 'Camping Gradina', price: '15–25 €', note: 'am Strand, südlich Sozopol' },
        { name: 'Kavatsi / Smokinya', price: '15–25 €', note: 'Eco-Camps, warmes Wasser' },
      ],
      highlights: ['Altstadt Sozopol', 'Harmani-Strand', 'Wasser ~24–25 °C'],
      night: ['Ruhige Altstadtbars', 'Sunny Beach 40 Min. nördlich für die laute Variante'],
      tip: 'Bargeld: Bulgarien hat 2026 den Euro, aber viele Camps nehmen nur Cash.',
    },
    {
      id: 's12',
      day: 12,
      from: 'Sozopol',
      to: 'Sinemorets',
      c1: [42.418, 27.696],
      c2: [42.062, 27.977],
      km: 80,
      h: 1.5,
      land: 'BG',
      type: 'standtag',
      camping: [
        { name: 'Camps Sinemorets / Veleka', price: '15–25 €', note: 'ruhige Naturstrände' },
      ],
      highlights: ['Veleka-Flussmündung', 'Silistar-Strand', 'Erholungstag'],
      night: [],
      tip: 'Der Gegenpol zu Sunny Beach. Hier auskurieren.',
    },
    {
      id: 's13',
      day: 13,
      from: 'Sinemorets',
      to: 'Sibiu',
      c1: [42.062, 27.977],
      c2: [45.798, 24.152],
      km: 550,
      h: 7,
      land: 'BG→RO',
      type: 'lang',
      camping: [
        { name: 'Camps um Sibiu / Cârțișoara', price: '20–30 €', note: 'Fuss des Transfăgărășan' },
      ],
      highlights: [
        'Donaubrücke Ruse–Giurgiu ~6 €',
        'Bukarest Durchfahrt oder Stopp',
        'Sibiu Altstadt',
      ],
      night: [
        'Bukarest: Control Club, Expirat, Pura Vida Sky Bar — Eintritt oft frei vor Mitternacht',
      ],
      tip: 'Härtester Fahrtag. Früh los, Bukarest umfahren wenn ihr nicht ausgeht.',
    },
    {
      id: 's14',
      day: 14,
      from: 'Sibiu',
      to: 'Transfăgărășan',
      c1: [45.798, 24.152],
      c2: [45.604, 24.617],
      km: 200,
      h: 4,
      land: 'RO',
      type: 'pass',
      camping: [
        {
          name: 'Camps bei Cârțișoara / Bâlea',
          price: '20–30 €',
          note: 'oben wird es kalt: 10–18 °C',
        },
      ],
      highlights: [
        'offen seit 12.06.2026, täglich 7–21 Uhr',
        'Bâlea-See 2042 m',
        'Bären am Strassenrand — nicht anhalten',
      ],
      night: [],
      tip: 'Alternative Transalpina (DN67C), weniger überlaufen.',
    },
    {
      id: 's15',
      day: 15,
      from: 'Transfăgărășan',
      to: 'Budapest',
      c1: [45.604, 24.617],
      c2: [47.497, 19.04],
      km: 700,
      h: 9,
      land: 'RO→HU',
      type: 'heimweg',
      optional: true,
      camping: [{ name: 'Camps um Budapest', price: '25–35 €', note: 'Transitnacht' }],
      highlights: ['reiner Transfer'],
      night: [],
      tip: 'PUFFERTAG. Ohne Tag 15+16 geht die Rechnung nicht auf — Rückweg ab Rumänien sind ~1400 km.',
    },
    {
      id: 's16',
      day: 16,
      from: 'Budapest',
      to: 'Bern',
      c1: [47.497, 19.04],
      c2: [46.95, 7.44],
      km: 900,
      h: 10,
      land: 'HU→AT→CH',
      type: 'heimweg',
      optional: true,
      camping: [],
      highlights: ['Transit Ungarn / Österreich'],
      night: [],
      tip: 'Mit dem T3 lieber in zwei Etappen mit einer Nacht in Österreich.',
    },
  ],
}

/**
 * Mautpflichten entlang der Route: der Ausgangspunkt der Vignettenliste.
 *
 * Vorlage und nicht Wahrheit - die Liste liegt im checks-Dokument und ist in der
 * App ergänzbar (siehe checks.ts). Von hier kommt sie beim ersten Start, und von
 * hier kommen die Einträge, wenn ein Bestand aus der Zeit gelesen wird, in der
 * das Dokument nur Haken je Name hielt.
 */
export const VIGNETTES: Vignette[] = [
  {
    country: 'Österreich',
    price: '12.80 € (10 Tage)',
    note: 'Karawankentunnel 8.80 € separat, je Richtung',
  },
  {
    country: 'Slowenien',
    price: '16 € (7 Tage, Kat. 2A)',
    note: 'Nur als Camper eingetragen = 2A. Sonst 2B = 32 €. evinjeta.dars.si',
  },
  {
    country: 'Kroatien',
    price: '~25 € Strecke',
    note: 'keine Vignette, Mautstationen, Karte geht',
  },
  { country: 'Bosnien', price: '—', note: 'keine Vignette. Grüne Karte auf PAPIER mitführen' },
  {
    country: 'Serbien',
    price: '~15 € Strecke',
    note: 'Grüne Karte für CH nicht nötig. Đerdap-Strasse mautfrei',
  },
  {
    country: 'Bulgarien',
    price: '7.67 € (Woche)',
    note: 'bgtoll.bg, digital. Donaubrücke ~6 € extra',
  },
  {
    country: 'Rumänien',
    price: '6 € (10 Tage)',
    note: 'kein 7-Tages-Produkt! rovinieta, gilt auch auf Landstrassen',
  },
  { country: 'Ungarn', price: '~14 € (10 Tage)', note: 'D1 wenn ≤7 Sitze. nemzetiutdij.hu' },
]

/**
 * Was der T3 an Ersatzteilen und Papieren dabei haben muss: der Ausgangspunkt
 * der Ersatzteilliste, ebenso ergänzbar wie die Vignetten.
 */
export const KIT: string[] = [
  'Keilriemen + Reserveriemen',
  'Kühlwasserschläuche + Schellen',
  'Thermostat',
  'Zündteile (Verteilerkappe, Kabel)',
  'Sicherungen-Sortiment',
  'Motoröl 2 l',
  'Kühlmittel 5 l',
  'Kraftstofffilter',
  'Ersatzrad + Wagenheber',
  'Pannenset',
  'Grüne Karte (Papier!)',
  'Reisepässe',
  'Bargeld BAM / RSD / BGN',
  'Warnweste ×2, Warndreieck, Verbandskasten',
]

/** Die Farbe der Kraftstoffkategorie, wie auch immer sie gerade heisst. */
const KRAFTSTOFF_FARBE = '#c9a227'

/** Die Kategorien, die von der Kraftstoffart unberührt bleiben. */
const WEITERE_KATEGORIEN: Category[] = [
  { key: 'Camping', color: '#2e7d8a' },
  { key: 'Essen', color: '#8c5a3c' },
  { key: 'Ausgehen', color: '#a8322d' },
  { key: 'Maut', color: '#5a6b4a' },
  { key: 'Eintritte', color: '#4a5b7a' },
  { key: 'Sonstiges', color: '#7a7266' },
]

/**
 * Ausgabenkategorien mit den Farben der Diagramme.
 *
 * Die erste hängt am Fahrzeug: sie heisst wie das, was getankt wird. Hier stand
 * fest „Benzin“, weil das Fahrzeug der ersten Reise einer ist - ein Diesel
 * buchte seinen Treibstoff damit unter „Benzin“ oder unter „Sonstiges“. Die
 * Farbe bleibt dieselbe, egal wie die Kategorie heisst: sie gehört der Stelle
 * im Diagramm, nicht dem Wort.
 */
export function categories(fuel: FuelKind): Category[] {
  return [{ key: FUEL_CATEGORY[fuel], color: KRAFTSTOFF_FARBE }, ...WEITERE_KATEGORIEN]
}

/**
 * Alte Kategorienamen und ihre heutige Entsprechung.
 *
 * Die Kategorie steht als Text in jeder Ausgabe, nicht als Verweis. Eine
 * Umbenennung in categories() lässt deshalb alles liegen, was vorher erfasst
 * wurde: der Farbpunkt in der Liste bliebe unsichtbar, weil die Farbtabelle den
 * Namen nicht mehr kennt, und im Diagramm liefe der alte Name als eigene
 * Restkategorie neben der neuen.
 *
 * ## Was diese Tabelle ist - und was sie nicht ist
 *
 * Sie ist eine Migration alter Namen und keine laufende Angleichung an das
 * Fahrzeug, das eine Reise gerade fährt. Die eine Umbenennung, die es gibt, ist
 * die Korrektur einer Annahme über ein einziges Auto: der T3 wurde für einen
 * Diesel gehalten, ist aber ein Benziner, und die damals erfassten Ausgaben
 * heissen seither „Benzin“. Das war richtig, solange es genau eine Reise gab,
 * und es gilt weiterhin genau für den Bestand von damals.
 *
 * Angewendet wird sie deshalb ausschliesslich auf ein Reisedokument aus der
 * Zeit vor dem Fahrzeug - erkennbar am fehlenden Trip.vehicle, siehe
 * ohneFahrzeug in lib/vehicle.ts. Der Weg läuft über currentCategory in
 * trip.ts; der Provider schreibt die betroffenen Ausgaben genau dann einmal um,
 * wenn er die Reise überführt, und danach ist der alte Name aus ihrem Bestand
 * verschwunden. Beim Import gilt dasselbe für die Datei: eine Datei, die ihr
 * Fahrzeug mitbringt, ist jünger als die Umbenennung, und ihre Kategorien
 * bleiben.
 *
 * ## Warum nicht nach der Kraftstoffart
 *
 * Weil die Kraftstoffart der heutige Zustand ist und nicht die Herkunft einer
 * Buchung. Eine Diesel-Reise, deren Fahrzeug jemand auf Benzin umstellt,
 * schrieb damit beim nächsten Laden alle „Diesel“-Buchungen unwiederbringlich
 * auf „Benzin“ um - Zurückstellen benannte nichts zurück -, und dasselbe traf
 * jede Reise, deren Dokument noch nicht geladen war: die leere Reise fährt
 * einen Benziner. Wer eine Buchung „Diesel“ genannt hat, hat sie so genannt;
 * nach dem Auswahlfeld umzuschreiben, was jemand geschrieben hat, ist keine
 * Migration, sondern Datenverlust in Raten.
 *
 * Umgekehrt wird ohnehin nichts umbenannt: eine Ausgabe „Benzin“ bleibt, was
 * sie ist. Sie könnte der Kanister für den Kocher gewesen sein.
 */
export const CATEGORY_RENAMES: Record<string, string> = {
  Diesel: 'Benzin',
}
